import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import {
  canWorkspacePurchaseAddOns,
  isOnFreePlan,
  isOnFreeTrial,
  type WorkspaceSubscriptionApiResponse,
} from '../billing-subscription.models';
import { BillingSubscriptionApiService } from './billing-subscription-api.service';

const TRIAL_ENDED_DISMISS_PREFIX = 'threadcrm:trial-ended-dismissed';

function trialEndedDismissKey(workspaceId: string, endedAt: string): string {
  return `${TRIAL_ENDED_DISMISS_PREFIX}:${workspaceId}:${endedAt}`;
}

@Injectable({ providedIn: 'root' })
export class BillingSubscriptionStateService {
  private readonly api = inject(BillingSubscriptionApiService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly dismissRevision = signal(0);

  readonly subscription = signal<WorkspaceSubscriptionApiResponse | null>(null);
  readonly isLoading = signal(false);
  readonly loadError = signal<string | null>(null);

  readonly isOnFreePlan = computed(() => isOnFreePlan(this.subscription()));

  readonly isOnFreeTrial = computed(() => isOnFreeTrial(this.subscription()));

  readonly canPurchaseAddOns = computed(() =>
    canWorkspacePurchaseAddOns(this.subscription()),
  );

  readonly upgradeBannerMode = computed<'free' | 'trial' | null>(() => {
    if (this.isOnFreeTrial()) {
      return 'trial';
    }
    if (this.isOnFreePlan()) {
      return 'free';
    }
    return null;
  });

  readonly showUpgradeBanner = computed(() => {
    if (!this.upgradeBannerMode()) {
      return false;
    }
    const url = this.router.url.split('?')[0] ?? '';
    return !url.startsWith('/subscription');
  });

  /** @deprecated Use showUpgradeBanner */
  readonly showFreePlanBanner = this.showUpgradeBanner;

  readonly showTrialEndedModal = computed(() => {
    this.dismissRevision();
    const notice = this.subscription()?.trialDowngradeNotice;
    const workspaceId = this.subscription()?.workspaceId;
    if (!notice || !workspaceId) {
      return false;
    }
    return !this.isTrialEndedDismissed(workspaceId, notice.endedAt);
  });

  constructor() {
    this.refresh();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.refresh();
      });
  }

  refresh(): void {
    this.isLoading.set(true);
    this.loadError.set(null);

    this.api.getSubscription().subscribe({
      next: (subscription) => {
        this.subscription.set(subscription);
        this.isLoading.set(false);
      },
      error: () => {
        this.loadError.set('Unable to load subscription.');
        this.isLoading.set(false);
      },
    });
  }

  dismissTrialEndedModal(): void {
    const sub = this.subscription();
    const notice = sub?.trialDowngradeNotice;
    if (!sub || !notice) {
      return;
    }

    localStorage.setItem(
      trialEndedDismissKey(sub.workspaceId, notice.endedAt),
      '1',
    );
    this.dismissRevision.update((value) => value + 1);
  }

  private isTrialEndedDismissed(workspaceId: string, endedAt: string): boolean {
    return (
      localStorage.getItem(trialEndedDismissKey(workspaceId, endedAt)) === '1'
    );
  }
}
